import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ProjectService } from '../../services/projectService';
import { Project } from '../../types/project';
import { Task } from '../../types/task';
import { formatDistanceToNow } from 'date-fns';
import { zhCN } from 'date-fns/locale';

/**
 * 分享项目详情页面 - V3版本
 * 该页面不需要认证，任何人都可以访问
 */
export const SharedProjectDetail: React.FC = () => {
  const { t } = useTranslation(['v3/projects', 'common']);
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();

  // 状态
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 加载分享项目数据
  useEffect(() => {
    const fetchSharedProject = async () => {
      if (!shareId) return;

      setLoading(true);
      setError(null);

      try {
        const response = await ProjectService.getSharedProject(shareId);
        setProject(response.project);
        setTasks(response.tasks || []);
      } catch (err) {
        console.error('Error fetching shared project:', err);
        setError(t('v3/projects:share.errors.cannotLoad'));
      } finally {
        setLoading(false);
      }
    };

    fetchSharedProject();
  }, [shareId]);

  // 格式化日期
  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: zhCN });
    } catch (error) {
      return t('common:dates.unknown');
    }
  };

  // 返回首页
  const handleBackToHome = () => {
    navigate('/');
  };

  // 登录
  const handleLogin = () => {
    navigate('/login');
  };

  // 加载中状态
  if (loading) {
    return (
      <div className="min-h-screen bg-ios-gray6 dark:bg-ios-dark-gray6 pt-12 pb-20">
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center mb-6">
            <button
              onClick={handleBackToHome}
              className="p-2 -ml-2 text-ios-blue dark:text-ios-dark-blue"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="h-7 w-32 bg-ios-gray5 dark:bg-ios-dark-gray4 rounded-md animate-pulse ml-2"></div>
          </div>

          <div className="bg-white dark:bg-ios-dark-gray5 rounded-ios-lg shadow-ios dark:shadow-ios-dark p-4 mb-6">
            <div className="h-8 w-3/4 bg-ios-gray5 dark:bg-ios-dark-gray4 rounded-md animate-pulse mb-3"></div>
            <div className="h-4 w-1/2 bg-ios-gray5 dark:bg-ios-dark-gray4 rounded-md animate-pulse mb-4"></div>
            <div className="h-20 bg-ios-gray5 dark:bg-ios-dark-gray4 rounded-md animate-pulse"></div>
          </div>

          <div className="h-6 w-32 bg-ios-gray5 dark:bg-ios-dark-gray4 rounded-md animate-pulse mb-4"></div>

          <div className="space-y-3">
            <div className="h-16 bg-white dark:bg-ios-dark-gray5 rounded-ios-lg shadow-ios dark:shadow-ios-dark animate-pulse"></div>
            <div className="h-16 bg-white dark:bg-ios-dark-gray5 rounded-ios-lg shadow-ios dark:shadow-ios-dark animate-pulse"></div>
            <div className="h-16 bg-white dark:bg-ios-dark-gray5 rounded-ios-lg shadow-ios dark:shadow-ios-dark animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  // 错误状态
  if (error || !project) {
    return (
      <div className="min-h-screen bg-ios-gray6 dark:bg-ios-dark-gray6 pt-12 pb-20">
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center mb-6">
            <button
              onClick={handleBackToHome}
              className="p-2 -ml-2 text-ios-blue dark:text-ios-dark-blue"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-ios-title font-semibold ml-2">{t('v3/projects:share.title')}</h1>
          </div>

          <div className="bg-white dark:bg-ios-dark-gray5 rounded-ios-lg shadow-ios dark:shadow-ios-dark p-6 mb-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-ios-title font-semibold mb-2">{t('v3/projects:share.errors.cannotLoadTitle')}</h2>
            <p className="text-ios-body text-ios-gray dark:text-ios-dark-gray mb-6">
              {error || t('v3/projects:share.errors.projectDeleted')}
            </p>
            <button
              onClick={handleLogin}
              className="w-full py-3 bg-ios-blue dark:bg-ios-dark-blue text-white rounded-ios-lg font-medium"
            >
              {t('v3/projects:share.loginToViewMore')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ios-gray6 dark:bg-ios-dark-gray6 pt-12 pb-20">
      <div className="max-w-lg mx-auto px-4">
        {/* 头部 */}
        <div className="flex items-center mb-6">
          <button
            onClick={handleBackToHome}
            className="p-2 -ml-2 text-ios-blue dark:text-ios-dark-blue"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-ios-title font-semibold ml-2">{t('v3/projects:share.title')}</h1>
        </div>

        {/* 项目信息卡片 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white dark:bg-ios-dark-gray5 rounded-ios-lg shadow-ios dark:shadow-ios-dark p-4 mb-6"
        >
          <h2 className="text-ios-large-title font-semibold mb-1">{project.title}</h2>
          <p className="text-ios-subhead text-ios-gray dark:text-ios-dark-gray mb-3">
            {t('v3/projects:info.updatedAt', { date: formatDate(project.updatedAt) })}
          </p>

          {project.context && (
            <p className="text-ios-body mb-4 whitespace-pre-wrap">
              {project.context}
            </p>
          )}

          <div className="flex justify-between items-center mt-4">
            <span className="px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-ios-caption rounded-ios-md">
              {t('v3/projects:share.publiclyShared')}
            </span>

            <button
              onClick={handleLogin}
              className="py-2 px-4 bg-ios-blue dark:bg-ios-dark-blue text-white rounded-ios-md font-medium text-ios-body"
            >
              {t('v3/projects:share.loginToViewMore')}
            </button>
          </div>
        </motion.div>

        {/* 任务列表 */}
        <h3 className="text-ios-title font-semibold mb-4">{t('v3/projects:share.projectTasks', { count: tasks.length })}</h3>

        {tasks.length === 0 ? (
          <div className="bg-white dark:bg-ios-dark-gray5 rounded-ios-lg shadow-ios dark:shadow-ios-dark p-4 flex items-center">
            <svg className="w-5 h-5 text-ios-blue dark:text-ios-dark-blue mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-ios-body text-ios-gray dark:text-ios-dark-gray">{t('v3/projects:emptyState.noTasks')}</span>
          </div>
        ) : (
          <div className="space-y-3">
            {tasks.map((task) => (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="bg-white dark:bg-ios-dark-gray5 rounded-ios-lg shadow-ios dark:shadow-ios-dark p-4"
              >
                <div className="flex items-center mb-1">
                  <span className={`text-ios-body font-medium ${task.done ? 'line-through text-ios-gray dark:text-ios-dark-gray' : ''}`}>
                    {task.title}
                  </span>
                  <div className="ml-auto flex space-x-1">
                    {task.vital && (
                      <span className="px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 text-ios-caption rounded-ios-md">
                        {t('v3/projects:taskPriority.vital')}
                      </span>
                    )}
                    {task.urgent && (
                      <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-ios-caption rounded-ios-md">
                        {t('v3/projects:taskPriority.urgent')}
                      </span>
                    )}
                  </div>
                </div>

                {task.notes && (
                  <p className={`text-ios-footnote text-ios-gray dark:text-ios-dark-gray line-clamp-2 ${task.done ? 'line-through' : ''}`}>
                    {task.notes}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        )}

        {/* 底部提示 */}
        <div className="mt-8 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/20 rounded-ios-lg p-4">
          <div className="flex">
            <svg className="w-5 h-5 text-ios-blue dark:text-ios-dark-blue mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-ios-subhead font-semibold text-ios-blue dark:text-ios-dark-blue">{t('v3/projects:share.readOnlyMode')}</h4>
              <p className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
                {t('v3/projects:share.readOnlyDescription')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
